import { styled } from "styled-components";

import {
  Button,
  colors,
  Divider,
  Pill,
  Spacer,
  Text,
} from "@vericus/cadmus-ui";

import {
  DuplicatePastAssessmentCallback,
  IPastAssessment,
  PreviewPastAssessmentCallback,
} from "@/data/templates";
import { AssessmentType, ExamTiming } from "@/generated/graphql";
import { selectAssessmentNoun } from "@/ui/task/assessment-utils";
import { formatCondensedDate } from "@/utils/datetime";

import { generateTags } from "./generate-tags";

interface Props {
  /**
   * Past assessment Details
   */
  pastAssessment: IPastAssessment;
  /**
   * Callback for when the user intends to duplicate the assessment
   * @param assessmentId
   */
  onDuplicate: DuplicatePastAssessmentCallback;
  /**
   * Callback for when the user intends to preview the assessment
   * @param assessmentId
   */
  onPreview: PreviewPastAssessmentCallback;
}

/**
 * A component to represent a past assessment (assignment or exam).
 */
export function PastAssessmentCard(props: Props) {
  const {
    pastAssessment: {
      settings,
      name,
      subject,
      instructionSheetVersionId,
      assessmentId,
    },
  } = props;

  const dueDate = settings?.dueDate != null ? new Date(settings.dueDate) : null;
  const examStartDate = settings?.examStartDate != null
    ? new Date(settings.examStartDate)
    : null;
  const examEndDate = settings?.examEndDate != null
    ? new Date(settings.examEndDate)
    : null;

  const assessmentNoun = selectAssessmentNoun(settings);

  return (
    <Container>
      <TopContainer>
        <Text kind="headingFour">{name}</Text>
        <Text kind={"label"}>{subject.name}</Text>
        <Spacer spacing={16} />

        {/* Assignment due date */}
        {settings.assessmentType === AssessmentType.Assignment && dueDate && (
          <Text kind="bodySm">
            <b>Due date:</b> {formatCondensedDate(dueDate)}
          </Text>
        )}

        {/* Exam window timing */}
        {settings?.examTiming === ExamTiming.Window && (
          <>
            {examStartDate && (
              <Text kind="systemMd" as="div">
                <b>{assessmentNoun} open:</b>{" "}
                {formatCondensedDate(examStartDate)}
              </Text>
            )}
            {examEndDate && (
              <Text kind="systemMd" as="div">
                <b>{assessmentNoun} close:</b>{" "}
                {formatCondensedDate(examEndDate)}
              </Text>
            )}
          </>
        )}

        {/* Exam live timing */}
        {settings?.examTiming === ExamTiming.Live && (
          <>
            {examStartDate && (
              <Text kind="systemMd" as="div">
                <b>{assessmentNoun} start:</b>{" "}
                {formatCondensedDate(examStartDate)}
              </Text>
            )}
          </>
        )}

        <Spacer spacing={12} />
        <Divider />
        <Spacer spacing={12} />
        <TagsContainer>
          {generateTags(props.pastAssessment).map((tag, index) => {
            return (
              <Pill key={index} color={tag.color} as="div" basic>
                <Text kind={"label"}>{tag.value}</Text>
              </Pill>
            );
          })}
        </TagsContainer>
      </TopContainer>
      <BottomContainer>
        <ControlBar>
          <PreviewButton
            kind={"outline"}
            onClick={() =>
              props.onPreview(props.pastAssessment, instructionSheetVersionId)
            }
          >
            Preview
          </PreviewButton>
          <DuplicateButton
            kind={"secondary"}
            onClick={() =>
              props.onDuplicate(assessmentId, name, instructionSheetVersionId)
            }
          >
            Duplicate
          </DuplicateButton>
        </ControlBar>
      </BottomContainer>
    </Container>
  );
}

const TopContainer = styled.div``;
const BottomContainer = styled.div`
  position: relative;
`;

// Default state when mouse is not hovering on the container
const ControlBar = styled.div`
  position: absolute;
  bottom: -10px;
  opacity: 0;
  width: 100%;
  display: flex;
  gap: 8px;
  background-color: white;
  z-index: 1;

  transition:
    bottom 0.2s ease-in,
    opacity 0.2s ease-in;

  & > * {
    flex-grow: 1;
  }
`;

const PreviewButton = styled(Button)`
  border-color: ${colors.black200};
  color: ${colors.black200};
  &:not(:disabled):after {
    box-shadow: none;
  }
`;

const DuplicateButton = styled(Button)`
  &:not(:disabled):hover:after {
    opacity: 0.5;
  }
`;

const Container = styled.div`
  border: 1px solid #5973a65c;
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1;

  &:focus-within,
  &:focus,
  &:active,
  &:hover {
    & ${ControlBar} {
      opacity: 1;
      bottom: 0px;
    }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
